import {
  MANAGEMENTS_STATE_ERROR,
  MANAGEMENTS_STATE_PENDING,
  MANAGEMENTS_STATE_REJECTED,
  MANAGEMENTS_STATE_RESOLVED,
  MANAGEMENTS_REGISTER_TYPE_REQUEST,
  MANAGEMENTS_SUBTYPE_UPDATE_POWERS_TEXT,
  MANAGEMENTS_SUBTYPE_UPDATE_HOLDER_TEXT,
  MANAGEMENTS_SUBTYPE_CONTRACT_END_TEXT,
  MANAGEMENTS_SUBTYPE_UPDATE_ADDRESS_TEXT,
  MANAGEMENTS_SUBTYPE_UPDATE_CONTACT_TEXT,
  MANAGEMENTS_REGISTER_TYPE_CLAIMS,
  MANAGEMENTS_SUBTYPE_UPDATE_CLAIM_TEXT,
  SERVICES_SUBTYPE_OPTIMIZE_COVERAGES,
  SERVICES_SUBTYPE_OPTIMIZE_POWER,
  SERVICES_SUBTYPE_OPTIMIZE_GAS,
  SERVICES_SUBTYPE_OPTIMIZE_BILLS,
  MANAGEMENTS_SUBTYPE_UPDATE_BANK_ACCOUNT_TEXT,
  MANAGEMENTS_SUBTYPE_UPDATE_GAS_FLOW_TEXT,
  MANAGEMENTS_STATE_OPEN,
  MANAGEMENTS_INCIDENCE_TEXT_UPDATE_CONTACT,
  MANAGEMENTS_INCIDENCE_TEXT_UPDATE_DELIVERY_DATA,
  MANAGEMENTS_STATE_PENDING_DISTRIBUTION,
  MANAGEMENTS_STATE_PENDING_COMMERCIAL,
  MANAGEMENTS_STATE_PENDING_CLIENT,
} from "../../../core/constants/constants";
import {
  Management,
  ManagementRequestInfo,
  GetManagementsRequestResponseResult,
  FormattedManagementsList,
  GetManagementDetailRequestResponse,
  ManagementDetail,
  RequestStateDescriptionData,
  ManagementsStateData,
} from "../../../core/models/managements/managements.model";
import { ManagementsList } from "../../../core/models/managements/managements.model";
import { formatStringDate } from "../../dateUtils/formatDate.util";
import theme from "../../../muiTheme";
import { ManagementUpdateCup } from "../../../core/models/cupsCifSelector/cupsCifSelector.model";
import { OwnerDataCups } from "../../../core/models/ownerData/ownerData.model";

const formatManagements = (managements: Management[]): ManagementsList[] =>
  managements.map(
    ({
      fechaPeticion,
      fechaVigencia,
      codigoGestion,
      estadoGestion,
      descripciónGestion,
      idGestion,
      cupsGestion,
    }) => ({
      managementId: idGestion,
      managementCode: codigoGestion,
      dateRequest: formatStringDate(fechaPeticion),
      effectiveDate: formatStringDate(fechaVigencia),
      managementState: estadoGestion,
      managementDescription: descripciónGestion,
      managementCups: cupsGestion,
    })
  );

const formatManagementsList = (
  responseResult: GetManagementsRequestResponseResult[]
): FormattedManagementsList[] =>
  responseResult.map(
    ({
      NumeroIncidencia,
      Fecha,
      Estado,
      TextoTipoRegistro,
      TextoSubTipoReclamacion,
      TextoIncidencia,
    }) => ({
      issueNumber: NumeroIncidencia,
      date: Fecha,
      state: Estado,
      recordTextType: TextoTipoRegistro,
      claimTextSubType: TextoSubTipoReclamacion,
      incidenceText: TextoIncidencia,
    })
  );

const formatManagementDetail = ({
  NumeroIncidencia,
  Fecha,
  FechaCierre,
  Estado,
  CodigoCups,
  Documentos,
  TextoSolucion,
  TextoIncidencia,
  TextoTipoRegistro,
}: GetManagementDetailRequestResponse): ManagementDetail => {
  return {
    refferenceNumber: NumeroIncidencia,
    registrarionDate: formatStringDate(Fecha || ""),
    closingDate: formatStringDate(FechaCierre || ""),
    cupsCode: CodigoCups,
    state: Estado,
    solutionText: TextoSolucion
      ? TextoSolucion.replace("\n", "").split("\r")
      : [],
    incidenceText: TextoIncidencia,
    documents: Documentos.map((doc) => ({
      IdCallCenterDocumento: doc.IdCallCenterDocumento,
      IdDocumento: doc.IdDocumento,
      FileName: doc.FileName,
      Nombre: doc.Nombre,
    })),
    registerType: TextoTipoRegistro,
  };
};

const formatOwnerDataCup = ({
  codeCups,
  address,
  idCups,
  idContract,
  cif,
  envirorment,
  contractCode,
  situation,
  date,
}: OwnerDataCups): ManagementUpdateCup => ({
  cupsCode: codeCups,
  supplyAddress: address,
  cupsId: idCups,
  cupsContractId: idContract,
  cupsAssociatedCif: cif,
  envirorment,
  cupsContractCode: contractCode,
  situation,
  date,
});

const getRegisterTypeId = (
  envirorment: string,
  registerType: string
): number => {
  if (envirorment === "Luz") {
    return registerType === MANAGEMENTS_REGISTER_TYPE_REQUEST ? 2 : 1;
  }

  return registerType === MANAGEMENTS_REGISTER_TYPE_REQUEST ? 3 : 4;
};

const getIncidenceIconTitle = (
  incedenceTexts: string[],
  incidenceText: string
) => {
  const text =
    incedenceTexts.find((incidenceMatchingText) =>
      incidenceText.includes(incidenceMatchingText)
    ) ?? "";

  switch (text) {
    case MANAGEMENTS_INCIDENCE_TEXT_UPDATE_CONTACT:
      return {
        title: "Actualizar datos de contacto",
        icon: "t-icon-AddressBook",
      };
    case MANAGEMENTS_INCIDENCE_TEXT_UPDATE_DELIVERY_DATA:
      return {
        title: "Actualizar dirección de correspondencia",
        icon: "t-icon-home",
      };
    default:
      return { title: "Otras gestiones", icon: "t-icon-warning" };
  }
};

const getManagementTitleText = (
  recordTextType: string,
  claimTextSubType: string
) =>
  recordTextType === MANAGEMENTS_REGISTER_TYPE_CLAIMS
    ? recordTextType
    : getManagementCodeTextIcon(claimTextSubType).title;

const getManagementCodeTextIcon = (
  managementText: string
): { title: string; icon: string } => {
  switch (managementText) {
    case MANAGEMENTS_SUBTYPE_UPDATE_POWERS_TEXT:
      return {
        title: "Modificacion de potencias",
        icon: "t-icon-electricite-big",
      };
    case MANAGEMENTS_SUBTYPE_UPDATE_GAS_FLOW_TEXT:
      return {
        title: "Modificación de caudal diario (Qd)",
        icon: "t-icon-gaz-big",
      };
    case MANAGEMENTS_SUBTYPE_UPDATE_HOLDER_TEXT:
      return {
        title: "Cambio de titular",
        icon: "t-icon-building",
      };
    case MANAGEMENTS_SUBTYPE_UPDATE_ADDRESS_TEXT:
      return {
        title: "Actualización Dirección de Correspondencia",
        icon: "t-icon-home",
      };
    case MANAGEMENTS_SUBTYPE_UPDATE_BANK_ACCOUNT_TEXT:
      return {
        title: "Cambio de domiciliación bancaria",
        icon: "t-icon-Bank",
      };
    case MANAGEMENTS_SUBTYPE_CONTRACT_END_TEXT:
      return {
        title: "Baja por cese de actividad",
        icon: "t-icon-power-off",
      };
    case MANAGEMENTS_SUBTYPE_UPDATE_CLAIM_TEXT:
      return {
        title: "Reclamación",
        icon: "t-icon-warning",
      };
    case MANAGEMENTS_SUBTYPE_UPDATE_CONTACT_TEXT:
      return {
        title: "Actualizar datos de contacto",
        icon: "t-icon-AddressBook",
      };
    case "Habilitar / Deshabilitar Factura en Papel":
      return {
        title: "Actualizar envío de factura",
        icon: "t-icon-file",
      };
    default:
      return { title: "Otras gestiones", icon: "t-icon-warning" };
  }
};

const getManagementStateData = (
  managementState: string
): ManagementsStateData => {
  switch (managementState) {
    case MANAGEMENTS_STATE_PENDING:
    case MANAGEMENTS_STATE_PENDING_DISTRIBUTION:
    case MANAGEMENTS_STATE_PENDING_COMMERCIAL:
      return {
        color: theme.palette.info.contrastText,
        backgroundColor: theme.palette.info.light,
        icon: "t-icon-clock",
        label: "Gestiones.Estados.En curso",
      };

    case MANAGEMENTS_STATE_PENDING_CLIENT:
      return {
        color: theme.palette.info.contrastText,
        backgroundColor: theme.palette.info.light,
        icon: "t-icon-clock",
        label: "Gestiones.Estados.Pendiente Respuesta Cliente",
      };
    case MANAGEMENTS_STATE_OPEN:
      return {
        color: theme.palette.info.contrastText,
        backgroundColor: theme.palette.info.light,
        icon: "t-icon-clock",
        label: "Gestiones.Estados.Abierta",
      };
    case MANAGEMENTS_STATE_RESOLVED:
      return {
        color: theme.palette.success.contrastText,
        backgroundColor: theme.palette.success.light,
        icon: "t-icon-check",
        label: "Gestiones.Estados.Resuelta",
      };
    case MANAGEMENTS_STATE_REJECTED:
      return {
        color: theme.palette.error.contrastText,
        backgroundColor: theme.palette.error.light,
        icon: "t-icon-close",
        label: "Gestiones.Estados.Rechazado",
      };
    default:
      return { color: "", backgroundColor: "", icon: "", label: "" };
  }
};

const getManagementsRequestTypeData = (
  requestType: number | string,
  requestState: string
) => {
  switch (requestType) {
    case MANAGEMENTS_SUBTYPE_UPDATE_CONTACT_TEXT:
    case MANAGEMENTS_INCIDENCE_TEXT_UPDATE_CONTACT:
      switch (requestState) {
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title:
              "Gestiones.Se ha actualizado con éxito los datos de contacto",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Gestiones.Parece que ha habido un error",
            description:
              "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return { title: "", description: "" };
      }

    case MANAGEMENTS_SUBTYPE_UPDATE_POWERS_TEXT:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: "Gestiones.Descripción final gestión",
            descriptionExtra: "",
          };
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: "Gestiones.Descripción final gestión",
            descriptionExtra: "",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title: "Gestiones.Se ha realizado con éxito el cambio de potencia",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title:
              "Gestiones.Lo sentimos, no se ha podido realizar el cambio de potencia",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Gestiones.Parece que ha habido un error",
            description:
              "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: "Gestiones.Descripción final gestión",
            descriptionExtra: "",
          };
      }

    case MANAGEMENTS_SUBTYPE_UPDATE_GAS_FLOW_TEXT:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: "Gestiones.Descripción final gestión",
            descriptionExtra: "",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title: "Gestiones.Se ha realizado con éxito el cambio de caudal",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title:
              "Gestiones.Lo sentimos, no se ha podido realizar el cambio de caudal",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Gestiones.Parece que ha habido un error",
            description:
              "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: "Gestiones.Descripción final gestión",
            descriptionExtra: "",
          };
      }

    case MANAGEMENTS_SUBTYPE_UPDATE_BANK_ACCOUNT_TEXT:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: "Gestiones.Descripción final gestión",
            descriptionExtra: "",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title:
              "Gestiones.Se ha realizado con éxito el cambio de domiciliación",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title:
              "Gestiones.Lo sentimos, no se ha podido realizar el cambio de domiciliación",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Gestiones.Parece que ha habido un error",
            description:
              "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: "Gestiones.Descripción final gestión",
            descriptionExtra: "",
          };
      }

    case MANAGEMENTS_SUBTYPE_UPDATE_CLAIM_TEXT:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Gestiones.Hemos recibido su reclamación",
            description: "Gestiones.Descripción final gestión",
            descriptionExtra: "",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title: "Gestiones.Hemos dado respuesta a su reclamación",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title:
              "Gestiones.Lo sentimos, no se ha podido procesar su reclamación",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Gestiones.Parece que ha habido un error",
            description:
              "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Gestiones.Hemos recibido su reclamación",
            description: "Gestiones.Descripción final gestión",
            descriptionExtra: "",
          };
      }

    case MANAGEMENTS_SUBTYPE_UPDATE_ADDRESS_TEXT:
    case MANAGEMENTS_INCIDENCE_TEXT_UPDATE_DELIVERY_DATA:
      switch (requestState) {
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title:
              "Gestiones.Se ha actualizado con éxito la dirección de correspondencia",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_PENDING:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: "Gestiones.Descripción final gestión",
            descriptionExtra: "",
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Gestiones.Parece que ha habido un error",
            description:
              "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title:
              "Gestiones.Se ha actualizado con éxito la dirección de correspondencia",
            description: "Gestiones.Descripción final gestión",
          };
      }

    case MANAGEMENTS_SUBTYPE_UPDATE_HOLDER_TEXT:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: "Gestiones.Descripción final gestión",
            descriptionExtra: "",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title: "Gestiones.Se ha realizado con éxito el cambio de titular",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title:
              "Gestiones.Lo sentimos, no se ha podido procesar su solicitud",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Gestiones.Parece que ha habido un error",
            description:
              "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: "Gestiones.Descripción final gestión",
            descriptionExtra: "",
          };
      }
    case MANAGEMENTS_SUBTYPE_CONTRACT_END_TEXT:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Gestiones.Hemos recibido su solicitud de baja",
            description: "Gestiones.Descripción final gestión",
            descriptionExtra: "",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title: "Gestiones.Su suministro ha sido dado de baja",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title: "Gestiones.Lo sentimos no hemos podido procesar su baja",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Gestiones.Parece que ha habido un error",
            description:
              "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Gestiones.Hemos recibido su solicitud de baja",
            description: "Gestiones.Descripción final gestión",
            descriptionExtra: "",
          };
      }

    // ---- Services
    case SERVICES_SUBTYPE_OPTIMIZE_COVERAGES:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Servicios.Orden de cobertura solicitada",
            description: "Servicios.Procesando orden de cobertura",
            descriptionExtra:
              "Servicios.Se enviará respuesta a todos los contactos asociados a este suministro.",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title: "Servicios.Orden de cobertura procesada con éxito",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title:
              "Servicios.Lo sentimos, no se ha podido procesar la orden de cobertura",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Servicios.Parece que ha habido un error",
            description:
              "Servicios.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Servicios.Orden de cobertura solicitada",
            description: "Servicios.Procesando orden de cobertura",
            descriptionExtra:
              "Servicios.Se enviará respuesta a todos los contactos asociados a este suministro.",
          };
      }
    case SERVICES_SUBTYPE_OPTIMIZE_POWER:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Servicios.Optimización de potencias solicitado",
            description: "Servicios.Procesando optimización de potencias",
            descriptionExtra:
              "Servicios.Se enviará respuesta a todos los contactos asociados a este suministro.",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title: "Servicios.Optimizador de potencias realizado correctamente",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title:
              "Servicios.Lo sentimos, no se ha podido realizar el optimizador de potencias",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Servicios.Parece que ha habido un error",
            description:
              "Servicios.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Servicios.Optimización de potencias solicitado",
            description: "Servicios.Procesando optimización de potencias",
            descriptionExtra:
              "Servicios.Se enviará respuesta a todos los contactos asociados a este suministro.",
          };
      }
    case SERVICES_SUBTYPE_OPTIMIZE_GAS:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Servicios.Optimización de caudal solicitado",
            description: "Servicios.Procesando optimización de caudal",
            descriptionExtra:
              "Servicios.Se enviará respuesta a todos los contactos asociados a este suministro.",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title: "Servicios.Optimizador de caudal realizado correctamente",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title:
              "Servicios.Lo sentimos, no se ha podido realizar el optimizador de caudal",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Servicios.Parece que ha habido un error",
            description:
              "Servicios.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Servicios.Optimización de caudal solicitado",
            description: "Servicios.Procesando optimización de caudal",
            descriptionExtra:
              "Servicios.Se enviará respuesta a todos los contactos asociados a este suministro.",
          };
      }
    case SERVICES_SUBTYPE_OPTIMIZE_BILLS:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Servicios.Simulación de facturas solicitado",
            description: "Gestiones.Descripción final gestión",
            descriptionExtra: "",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title: "Servicios.Simulador de facturas realizado correctamente",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title:
              "Servicios.Lo sentimos, no se ha podido realizar la simulación de facturas",
            description: "Gestiones.Descripción final gestión",
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Servicios.Parece que ha habido un error",
            description:
              "Servicios.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Servicios.Simulación de facturas solicitado",
            description: "Gestiones.Descripción final gestión",
            descriptionExtra: "",
          };
      }

    default:
      return { title: "", description: "" };
  }
};

const initialManagementRequestInfo: ManagementRequestInfo = {
  refferenceNumber: "-",
  registrarionDate: "-",
  closingDate: "-",
};

const initialRequestStateData: RequestStateDescriptionData = {
  title: "",
  description: "",
  descriptionExtra: "",
};

const initialManagementDetail: ManagementDetail = {
  refferenceNumber: null,
  registrarionDate: "",
  closingDate: null,
  cupsCode: "",
  state: "",
  solutionText: null,
  documents: [],
  incidenceText: "",
  registerType: "",
};

const initialDocumentData = {
  DocumentoData: "",
  FileName: "",
  IdDocumento: "",
  Nombre: "",
};

const getBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result?.toString());
    reader.onerror = reject;
  });

const incedenceTexts = [
  MANAGEMENTS_INCIDENCE_TEXT_UPDATE_CONTACT,
  MANAGEMENTS_INCIDENCE_TEXT_UPDATE_DELIVERY_DATA,
];

export {
  formatManagements,
  formatManagementsList,
  formatManagementDetail,
  formatOwnerDataCup,
  getManagementCodeTextIcon,
  initialManagementRequestInfo,
  initialManagementDetail,
  initialRequestStateData,
  getManagementStateData,
  getManagementsRequestTypeData,
  getRegisterTypeId,
  getBase64,
  getManagementTitleText,
  initialDocumentData,
  getIncidenceIconTitle,
  incedenceTexts,
};
